import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Pause, Play, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { HubHistoryEvent } from '@aiai/shared';
import { fetchHubHistory } from '../api';
import type { FeedEntry } from './feed';
import { feedFromHistoryEvent, type HubEffect } from './hubLive';
import {
  effectFor,
  PLAYBACK_PERIODS,
  PLAYBACK_SPEEDS,
  HISTORY_MAX_WINDOW_MS,
  playbackClock,
  playbackDurationMs,
  REPLAY_MS,
  replaySummary,
  scheduleReplay,
  type PlaybackPeriodId,
  type PlaybackSpeed,
  type ScheduledEvent,
} from './replay';
import type { ReplayOffer } from './useReplay';
import { usePrefersReducedMotion } from './scene/usePrefersReducedMotion';

export type PlaybackMode = { kind: 'offer'; offer: ReplayOffer } | { kind: 'playback' };

interface Callbacks {
  /** A replayed effect to play on its planet. */
  onEffect: (effect: HubEffect) => void;
  /** Feed lines for replayed events, stamped with their real times. */
  onFeed: (entries: FeedEntry[]) => void;
  onClose: () => void;
  /** Play pressed from the start: the page sends the camera to fit the universe. */
  onPlay: () => void;
  hrefFor: (event: HubHistoryEvent) => string | null;
}

interface Props extends Callbacks {
  /** `offer`: the "While you were away" replay (20 s at 1x, no choices). `playback`: any recent period at 1-4x. */
  mode: PlaybackMode;
  /** Playback mode: the period changed, so the lines the previous period put on the feed can go. */
  onReset?: () => void;
}

const DEFAULT_PERIOD: PlaybackPeriodId = '24h';
const DEFAULT_SPEED: PlaybackSpeed = 2;
/**
 * A returned `since` more than this past the expected start means the API truncated the history.
 * The expected start is the requested one, or `until - 7 d` when the server clamped it (a client
 * clock behind the server's).
 */
const TRUNCATION_SLACK_MS = 60_000;

/**
 * On phones the card sits in flow right below the HUD row (both are `order-first` in the hub's
 * column), so it covers neither the HUD buttons nor the Live button; from sm up it floats over the scene.
 */
const CARD_CLASS =
  'order-first mx-2 mb-2 rounded-lg border border-line bg-surface-1 p-3 shadow-lg focus:outline-none sm:absolute sm:left-1/2 sm:top-3 sm:z-20 sm:mx-0 sm:mb-0 sm:w-[min(92%,24rem)] sm:-translate-x-1/2';

/**
 * The replay clock both modes share. `elapsed` is the position in 1x ms on [0, durationMs]; each tick
 * advances it by the wall time since the last tick times `speed` and fires every scheduled event it
 * passed. A speed change only changes the rate: the position and the fired set stay. A new schedule
 * keeps the position and counts everything before it as played (no burst).
 */
function useReplayClock(
  schedule: ScheduledEvent[],
  durationMs: number,
  speed: number,
  idPrefix: string,
  callbacks: Pick<Callbacks, 'onEffect' | 'onFeed' | 'hrefFor'>,
) {
  const reduced = usePrefersReducedMotion();
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(0);
  const fired = useRef(new Set<number>());
  // Latest callbacks and speed, so the ticking loop never restarts when the parent re-renders.
  const cb = useRef(callbacks);
  cb.current = callbacks;
  const speedRef = useRef(speed);
  speedRef.current = speed;

  // Declared before the ticking effect, so a new schedule is re-marked before it ticks.
  useEffect(() => {
    if (elapsedRef.current > durationMs) {
      elapsedRef.current = durationMs;
      setElapsed(durationMs);
    }
    fired.current = new Set(schedule.flatMap((s, i) => (s.offsetMs < elapsedRef.current ? [i] : [])));
  }, [schedule, durationMs]);

  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const tick = () => {
      const t = performance.now();
      const next = Math.min(elapsedRef.current + (t - last) * speedRef.current, durationMs);
      last = t;
      elapsedRef.current = next;
      const feed: FeedEntry[] = [];
      schedule.forEach((s, i) => {
        if (s.offsetMs > next || fired.current.has(i)) return;
        fired.current.add(i);
        const effect = effectFor(s.event, `${idPrefix}${i}`, performance.now());
        if (effect) cb.current.onEffect(effect);
        feed.push(feedFromHistoryEvent(s.event, `${idPrefix}${i}:${s.event.type}`, cb.current.hrefFor(s.event)));
      });
      if (feed.length > 0) cb.current.onFeed(feed);
      setElapsed(next);
      if (next >= durationMs) setPlaying(false);
    };
    if (reduced) {
      const id = window.setInterval(tick, 50);
      return () => window.clearInterval(id);
    }
    let raf = 0;
    const loop = () => {
      tick();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, reduced, schedule, durationMs, idPrefix]);

  const seek = (to: number) => {
    const v = Math.min(Math.max(to, 0), durationMs);
    elapsedRef.current = v;
    // Everything before the new position counts as already played (no burst); the rest can play again.
    fired.current = new Set(schedule.flatMap((s, i) => (s.offsetMs < v ? [i] : [])));
    setElapsed(v);
  };

  /** Back to the start, paused, nothing played. */
  const reset = () => {
    setPlaying(false);
    elapsedRef.current = 0;
    fired.current = new Set();
    setElapsed(0);
  };

  return { playing, setPlaying, elapsed, finished: elapsed >= durationMs, seek, reset };
}

/** Pause/Play, the scrubber and Close: the row both modes show while replaying. */
function Transport({
  playing, disabled = false, elapsed, durationMs, valueText, onToggle, onSeek, onClose,
}: {
  playing: boolean; disabled?: boolean; elapsed: number; durationMs: number; valueText: string;
  onToggle: () => void; onSeek: (to: number) => void; onClose: () => void;
}) {
  return (
    <div className="mt-2 flex items-center gap-2">
      <button type="button" className="btn-secondary" onClick={onToggle} disabled={disabled}>
        {playing ? (
          <>
            <Pause className="h-4 w-4" aria-hidden="true" />
            Pause
          </>
        ) : (
          <>
            <Play className="h-4 w-4" aria-hidden="true" />
            Play
          </>
        )}
      </button>
      <input
        type="range"
        aria-label="Replay position"
        aria-valuetext={valueText}
        min={0}
        max={Math.round(durationMs)}
        step={50}
        value={Math.round(elapsed)}
        disabled={disabled}
        onChange={(e) => onSeek(Number(e.target.value))}
        className="min-w-0 flex-1 accent-ink"
      />
      <button type="button" className="btn-secondary" onClick={onClose}>
        <X className="h-4 w-4" aria-hidden="true" />
        Close
      </button>
    </div>
  );
}

/** "While you were away": the offer first, then a 20 s replay at 1x. */
function OfferReplay({ offer, onEffect, onFeed, onClose, onPlay, hrefFor }: Callbacks & { offer: ReplayOffer }) {
  const schedule = useMemo(() => scheduleReplay(offer.events, offer.since, offer.until), [offer]);
  const [started, setStarted] = useState(false);
  const { playing, setPlaying, elapsed, finished, seek } = useReplayClock(schedule, REPLAY_MS, 1, 'replay:', {
    onEffect,
    onFeed,
    hrefFor,
  });

  const play = () => {
    if (!started) setStarted(true);
    setPlaying(true);
    onPlay();
  };

  const spanMs = Date.parse(offer.until) - Date.parse(offer.since);
  const realMs = Date.parse(offer.since) + (Number.isFinite(spanMs) && spanMs > 0 ? (elapsed / REPLAY_MS) * spanMs : 0);
  // Always HH:MM, as before.
  const clock = playbackClock(realMs, 0);

  return (
    <div role="region" aria-label="While you were away" className={CARD_CLASS}>
      {!started ? (
        <>
          <h2 className="text-sm font-semibold text-ink">While you were away</h2>
          <p className="mt-1 text-sm text-ink-muted">{offer.summary.text}</p>
          <div className="mt-3 flex items-center gap-2">
            <button type="button" className="btn-primary" onClick={play}>
              <Play className="h-4 w-4" aria-hidden="true" />
              Play
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Dismiss
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-ink">While you were away</h2>
            <span data-testid="replay-clock" className="ml-auto font-mono text-xs tabular-nums text-ink-muted">
              {clock}
            </span>
          </div>
          <p aria-live="polite" className="mt-2 text-sm text-ink-muted">
            {finished ? 'Replay finished' : offer.summary.text}
          </p>
          <Transport
            playing={playing}
            elapsed={elapsed}
            durationMs={REPLAY_MS}
            valueText={clock}
            onToggle={() => (playing ? setPlaying(false) : finished ? (seek(0), setPlaying(true)) : setPlaying(true))}
            onSeek={seek}
            onClose={onClose}
          />
        </>
      )}
    </div>
  );
}

/** A row of chips that is a native radio group, so the arrow keys move the choice. */
function ChipGroup<T extends string | number>({
  label, options, value, onChange,
}: { label: string; options: ReadonlyArray<{ value: T; label: string }>; value: T; onChange: (v: T) => void }) {
  const name = `playback-${label.toLowerCase()}${useId().replace(/:/g, '')}`;
  return (
    <div role="radiogroup" aria-label={label} className="flex items-center gap-2">
      {options.map((o) => (
        <label key={o.value} className="cursor-pointer">
          <input
            type="radio"
            name={name}
            value={String(o.value)}
            checked={o.value === value}
            onChange={() => onChange(o.value)}
            className="peer sr-only"
          />
          <span className="flex min-h-6 items-center rounded-[5px] border border-line-strong px-2 font-display text-2xs font-bold uppercase tracking-[0.08em] text-ink-muted hover:text-ink peer-checked:border-ink peer-checked:bg-ink peer-checked:text-surface-1 peer-focus-visible:ring-2 peer-focus-visible:ring-signal/60">
            {o.label}
          </span>
        </label>
      ))}
    </div>
  );
}

const PERIOD_OPTIONS = PLAYBACK_PERIODS.map((p) => ({ value: p.id as PlaybackPeriodId, label: p.label }));
const SPEED_OPTIONS = PLAYBACK_SPEEDS.map((s) => ({ value: s as PlaybackSpeed, label: `${s}\u00d7` }));
const EMPTY_SCHEDULE: ScheduledEvent[] = [];

/** Playback: any recent period (6 h to 7 d, ending now) at 1-4x, where 1x plays a day in a minute. */
function PeriodPlayback({ onEffect, onFeed, onClose, onPlay, hrefFor, onReset }: Callbacks & { onReset?: () => void }) {
  const [periodId, setPeriodId] = useState<PlaybackPeriodId>(DEFAULT_PERIOD);
  const [speed, setSpeed] = useState<PlaybackSpeed>(DEFAULT_SPEED);
  const period = PLAYBACK_PERIODS.find((p) => p.id === periodId) ?? PLAYBACK_PERIODS[1];

  const query = useQuery({
    queryKey: ['hub-history', periodId],
    queryFn: async () => {
      const requested = new Date(Date.now() - period.ms).toISOString();
      return { requested, history: await fetchHubHistory(requested) };
    },
    staleTime: 60_000,
    // The data must never change under a playing card: no background refetch, and no cache that a
    // reopened card could show and then refetch over (each open fetches fresh).
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const data = query.data;

  const view = useMemo(() => {
    if (!data) return null;
    const { history, requested } = data;
    const parsedSince = Date.parse(history.since);
    const since = Number.isFinite(parsedSince) ? parsedSince : Date.parse(requested);
    const span = Date.parse(history.until) - since;
    const spanMs = Number.isFinite(span) && span > 0 ? span : period.ms;
    const durationMs = playbackDurationMs(spanMs);
    return {
      since,
      spanMs,
      durationMs,
      schedule: scheduleReplay(history.events, history.since, history.until, durationMs),
      summary: replaySummary(history.events),
      empty: history.events.length === 0,
      truncated:
        since > Math.max(Date.parse(requested), Date.parse(history.until) - HISTORY_MAX_WINDOW_MS) + TRUNCATION_SLACK_MS,
    };
  }, [data, period.ms]);

  const durationMs = view?.durationMs ?? playbackDurationMs(period.ms);
  const { playing, setPlaying, elapsed, finished, seek, reset } = useReplayClock(
    view?.schedule ?? EMPTY_SCHEDULE,
    durationMs,
    speed,
    `replay:${periodId}:`,
    { onEffect, onFeed, hrefFor },
  );

  // Focus moves into the card when it opens, so Esc works right after the HUD button.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, []);

  // Esc from inside the card ends playback, and only playback (the LiveDrawer pattern): the peek
  // card's document-level Esc stays put, and an open modal keeps its own Esc.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    if (!cardRef.current?.contains(e.target as Node)) return;
    if (document.querySelector('[aria-modal="true"]')) return;
    e.stopPropagation();
    onClose();
  };

  const changePeriod = (id: PlaybackPeriodId) => {
    if (id === periodId) return;
    reset();
    setPeriodId(id);
    onReset?.();
  };

  const toggle = () => {
    if (playing) {
      setPlaying(false);
      return;
    }
    const fromStart = finished || elapsed === 0;
    if (finished) seek(0);
    setPlaying(true);
    // Starting from the top fits the universe; resuming leaves the camera where the user put it.
    if (fromStart) onPlay();
  };

  const clock = view ? playbackClock(view.since + (elapsed / durationMs) * view.spanMs, period.ms) : '\u2014';

  let status: string;
  if (query.isError) status = 'Could not load the history.';
  else if (!view) status = 'Loading history\u2026';
  else if (view.empty) status = 'Nothing happened in this period.';
  else if (finished) status = 'Playback finished';
  else status = view.summary.text;

  return (
    <div ref={cardRef} role="region" aria-label="Playback" tabIndex={-1} onKeyDown={onKeyDown} className={CARD_CLASS}>
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-ink">Playback</h2>
        <span data-testid="replay-clock" className="ml-auto font-mono text-xs tabular-nums text-ink-muted">
          {clock}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <ChipGroup label="Period" options={PERIOD_OPTIONS} value={periodId} onChange={changePeriod} />
        <ChipGroup label="Speed" options={SPEED_OPTIONS} value={speed} onChange={setSpeed} />
      </div>
      <p aria-live="polite" className="mt-2 text-sm text-ink-muted">
        {status}
      </p>
      {view?.truncated && (
        <p className="mt-0.5 text-2xs text-ink-muted">(showing from {playbackClock(view.since, period.ms)})</p>
      )}
      <Transport
        playing={playing}
        disabled={!view || view.empty}
        elapsed={elapsed}
        durationMs={durationMs}
        valueText={clock}
        onToggle={toggle}
        onSeek={seek}
        onClose={onClose}
      />
    </div>
  );
}

/**
 * The replay card. In offer mode it is "While you were away" (20 s at 1x, no choices); in playback mode
 * it replays any recent period at a chosen speed. Both run the same clock over `scheduleReplay`, play each
 * event as an effect on its planet and hand its feed line to the page.
 */
export function PlaybackCard({ mode, onReset, ...callbacks }: Props) {
  return mode.kind === 'offer' ? (
    <OfferReplay offer={mode.offer} {...callbacks} />
  ) : (
    <PeriodPlayback {...callbacks} onReset={onReset} />
  );
}
